import { FC, useCallback, useMemo, useState } from 'react';

import type { ActivityCounts, ActivityRange, TeamActivityMember } from '@/api/gamma-team/types';
import {
  Badge,
  Button,
  ColumnsPopover,
  EmptyState,
  getTableMinWidth,
  Select,
  Table,
  type TableColumn,
  type TableRow,
  theme,
  Typography,
} from '@/design-system';

import {
  formatEntryTime,
  formatEntryTimestamp,
} from '../../user-profile/modules/history-tab/format-entry-time';
import { NameStack, Section, SectionActions, SectionHeader, SectionTitle } from './styled';
import {
  buildTeamActivityCsv,
  downloadCsv,
  TEAM_COUNT_COLUMNS,
  teamActivityCsvFileName,
} from './team-activity-csv';
import {
  DEFAULT_TEAM_SORT,
  isInactiveMember,
  latestActivityAt,
  memberName,
  nextTeamSort,
  sortTeamMembers,
  type TeamSort,
  type TeamSortKey,
} from './team-sorting';
import {
  countIdleMembers,
  filterMembersByView,
  type IdleState,
  idleState,
  MEMBER_VIEW_OPTIONS,
  type MemberView,
  memberView,
} from './team-trends';

export const TOTAL_ROW_LABEL = 'Total';
export const FORMER_BADGE_LABEL = 'Former';
export const MEMBERS_HEADING = 'Team members';
export const EXPORT_CSV_LABEL = 'Export CSV';
const NAME_COLUMN = 'name';
const LAST_ACTIVE_COLUMN = 'lastActiveAt';
const COLUMNS_STORAGE_KEY = 'gp.teamActivity.columns';

export const MEMBER_VIEW_LABEL = 'Show';

interface TeamTableRow extends TableRow {
  label: string;
  caption: string | null;
  counts: ActivityCounts;
  lastActiveAt: string | null;
  former: boolean;
  idle: IdleState;
  isTotal: boolean;
}

export const idleBadgeLabel = (idle: IdleState): string | null => {
  if (idle.kind === 'idle') return `Idle ${idle.days}d`;
  if (idle.kind === 'never') return 'No activity';
  return null;
};

const NameCell: FC<{ row: TeamTableRow }> = ({ row }) => (
  <NameStack>
    <Typography
      variant="body3"
      fontWeight={row.isTotal ? 600 : 500}
      color={theme.colors.text.primary}
      noWrap
    >
      {row.label}
      {row.former && (
        <>
          {' '}
          <Badge label={FORMER_BADGE_LABEL} color="default" variant="subtle" size="sm" />
        </>
      )}
      {idleBadgeLabel(row.idle) && (
        <>
          {' '}
          <Badge label={idleBadgeLabel(row.idle) ?? ''} color="yellow" variant="subtle" size="sm" />
        </>
      )}
    </Typography>
    {row.caption && (
      <Typography variant="body4" color={theme.colors.text.secondary} noWrap>
        {row.caption}
      </Typography>
    )}
  </NameStack>
);

const CountCell: FC<{ row: TeamTableRow; column: keyof ActivityCounts }> = ({ row, column }) => {
  const value = row.counts[column];
  return (
    <Typography
      variant="body3"
      fontWeight={row.isTotal ? 600 : 400}
      color={value === 0 && !row.isTotal ? theme.colors.text.secondary : theme.colors.text.primary}
    >
      {value.toLocaleString()}
    </Typography>
  );
};

const LastActiveCell: FC<{ row: TeamTableRow }> = ({ row }) => {
  if (row.isTotal) return null;
  if (!row.lastActiveAt) {
    return (
      <Typography variant="body3" color={theme.colors.text.secondary}>
        —
      </Typography>
    );
  }
  return (
    <Typography
      variant="body3"
      color={theme.colors.text.primary}
      title={formatEntryTimestamp(row.lastActiveAt)}
      noWrap
    >
      {formatEntryTime(row.lastActiveAt)}
    </Typography>
  );
};

const ALL_COLUMNS: TableColumn<TeamTableRow>[] = [
  {
    key: NAME_COLUMN,
    header: 'Team member',
    sortable: true,
    minWidth: 200,
    pinned: 'start',
    cell: (row) => <NameCell row={row} />,
  },
  ...TEAM_COUNT_COLUMNS.map(
    ({ key, header, shortHeader, description }): TableColumn<TeamTableRow> => ({
      key,
      header: shortHeader ?? header,
      headerTooltip: description,
      align: 'right',
      sortable: true,
      width: 104,
      cell: (row) => <CountCell row={row} column={key} />,
    }),
  ),
  {
    key: LAST_ACTIVE_COLUMN,
    header: 'Last active',
    headerTooltip: 'Most recent entry or deal move, in any period',
    align: 'right',
    sortable: true,
    width: 120,
    cell: (row) => <LastActiveCell row={row} />,
  },
];

const COLUMN_OPTIONS = [
  { key: NAME_COLUMN, label: 'Team member', locked: true },
  ...TEAM_COUNT_COLUMNS.map(({ key, header }) => ({ key, label: header })),
  { key: LAST_ACTIVE_COLUMN, label: 'Last active' },
];

const ALL_COLUMN_KEYS = ALL_COLUMNS.map(({ key }) => key);

const readStoredColumns = (): string[] => {
  try {
    const raw = window.localStorage.getItem(COLUMNS_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(parsed)) return ALL_COLUMN_KEYS;
    const known = parsed.filter((key): key is string => ALL_COLUMN_KEYS.includes(key));
    return known.length > 0 ? known : ALL_COLUMN_KEYS;
  } catch {
    return ALL_COLUMN_KEYS;
  }
};

const useVisibleColumns = () => {
  const [visible, setVisible] = useState<string[]>(readStoredColumns);
  const update = useCallback((next: string[]) => {
    setVisible(next);
    try {
      window.localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(next));
    } catch {
      return;
    }
  }, []);
  return [visible, update] as const;
};

export const NAME_NOT_SET_CAPTION = 'Name not set';

const findSharedNames = (members: readonly TeamActivityMember[]): ReadonlySet<string> => {
  const seen = new Set<string>();
  const shared = new Set<string>();
  for (const member of members) {
    const name = memberName(member);
    if (seen.has(name)) shared.add(name);
    seen.add(name);
  }
  return shared;
};

const memberCaption = (
  member: TeamActivityMember,
  sharedNames: ReadonlySet<string>,
): string | null => {
  if (!member.name?.trim()) return member.email ? NAME_NOT_SET_CAPTION : null;
  return sharedNames.has(memberName(member)) ? (member.email ?? null) : null;
};

export const listTeamMembers = (
  members: readonly TeamActivityMember[],
  sort: TeamSort,
  view: MemberView,
  isSelection: boolean,
  now: Date,
): TeamActivityMember[] =>
  sortTeamMembers(isSelection ? members : filterMembersByView(members, view, now), sort);

interface TeamTableProps {
  members: TeamActivityMember[];
  total: ActivityCounts | null;
  range?: ActivityRange;
  isLoading: boolean;
  isSelection?: boolean;
  sort?: TeamSort;
  onSortChange?: (_sort: TeamSort) => void;
  view?: MemberView;
  onViewChange?: (_view: MemberView) => void;
  selectedMemberId?: string | null;
  now?: Date;
  onSelectMember: (_userId: string) => void;
}

export const TeamTable: FC<TeamTableProps> = ({
  members,
  total,
  range,
  isLoading,
  isSelection = false,
  sort: controlledSort,
  onSortChange,
  view: controlledView,
  onViewChange,
  selectedMemberId = null,
  now: providedNow,
  onSelectMember,
}) => {
  const [innerSort, setInnerSort] = useState(DEFAULT_TEAM_SORT);
  const [innerView, setInnerView] = useState<MemberView>(memberView.Active);
  const [visibleColumns, setVisibleColumns] = useVisibleColumns();
  const fallbackNow = useMemo(() => new Date(), []);
  const now = providedNow ?? fallbackNow;
  const sort = controlledSort ?? innerSort;
  const view = controlledView ?? innerView;
  const setSort = (next: TeamSort) => (onSortChange ?? setInnerSort)(next);
  const setView = (next: MemberView) => (onViewChange ?? setInnerView)(next);

  const inactiveCount = useMemo(() => members.filter(isInactiveMember).length, [members]);
  const idleCount = useMemo(() => countIdleMembers(members, now), [members, now]);

  const columns = useMemo(
    () => ALL_COLUMNS.filter(({ key }) => key === NAME_COLUMN || visibleColumns.includes(key)),
    [visibleColumns],
  );

  const rows = useMemo((): TeamTableRow[] => {
    const sharedNames = findSharedNames(members);
    const memberRows = listTeamMembers(members, sort, view, isSelection, now).map(
      (member): TeamTableRow => ({
        key: member.userId,
        label: memberName(member),
        caption: memberCaption(member, sharedNames),
        counts: member,
        lastActiveAt: latestActivityAt(member),
        former: member.former,
        idle: idleState(member, now),
        isTotal: false,
        highlight: member.userId === selectedMemberId,
        onClick: () => onSelectMember(member.userId),
      }),
    );

    if (!total || memberRows.length === 0) return memberRows;

    return [
      ...memberRows,
      {
        key: 'total',
        label: TOTAL_ROW_LABEL,
        caption: null,
        counts: total,
        lastActiveAt: null,
        former: false,
        idle: { kind: 'unknown' },
        isTotal: true,
      },
    ];
  }, [members, total, sort, view, isSelection, now, selectedMemberId, onSelectMember]);

  const exportCsv = () =>
    downloadCsv(
      buildTeamActivityCsv(sortTeamMembers(members, sort), total),
      teamActivityCsvFileName(range),
    );

  const activeCount = members.length - inactiveCount;

  return (
    <Section aria-label={MEMBERS_HEADING}>
      <SectionHeader>
        <SectionTitle>
          <Typography variant="body1" fontWeight={600}>
            {MEMBERS_HEADING}
          </Typography>
          {!isLoading && members.length > 0 && (
            <Typography variant="body3" color={theme.colors.text.secondary}>
              {`${activeCount} of ${members.length} active in this period`}
            </Typography>
          )}
        </SectionTitle>
        <SectionActions>
          {!isSelection && (
            <Select
              size="sm"
              ariaLabel={MEMBER_VIEW_LABEL}
              value={view}
              options={MEMBER_VIEW_OPTIONS.map((option) => ({
                ...option,
                supportText:
                  option.value === memberView.Idle
                    ? `${idleCount} ${idleCount === 1 ? 'member' : 'members'}`
                    : option.value === memberView.Active
                      ? `${members.length - inactiveCount} of ${members.length}`
                      : undefined,
              }))}
              onChange={(value) => setView(value as MemberView)}
            />
          )}
          <ColumnsPopover
            columns={COLUMN_OPTIONS}
            visible={visibleColumns}
            onVisibleChange={setVisibleColumns}
          />
          <Button
            variant="outline"
            color="tertiary"
            size="sm"
            disabled={isLoading || members.length === 0}
            onClick={exportCsv}
          >
            {EXPORT_CSV_LABEL}
          </Button>
        </SectionActions>
      </SectionHeader>

      <Table
        columns={columns}
        rows={rows}
        size="sm"
        sortKey={sort.key}
        sortDirection={sort.direction}
        onSort={(key) => setSort(nextTeamSort(sort, key as TeamSortKey))}
        isLoading={isLoading}
        skeletonRowCount={6}
        minTableWidth={getTableMinWidth(columns)}
        emptyState={
          <EmptyState
            variant="tertiary"
            role="status"
            title={
              members.length === 0
                ? 'No team members in this period.'
                : view === memberView.Idle
                  ? 'No one has been idle for a week or more.'
                  : 'No one on the team was active in this period.'
            }
          />
        }
      />
    </Section>
  );
};
